import "./ChildOrdersDialogComponent.scss";
import template from "./ChildOrdersDialogComponent.html";
import {IComponentOptions, IOnDestroy, IOnInit, IRootElementService, ITimeoutService} from "angular";
import {IModalInstanceService} from "angular-ui-bootstrap";
import {EquityOrderWizardStore} from "../../../portfolio/templates/ticket/place-equity-order-wizard/store/EquityOrderWizardStore";
import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {isNotNull} from "../../../js/util/UtilFunctions";
import {EquityPosition, PortfolioPositions} from "../../../portfolio/models/Portfolio";
import {PortfolioService} from "../../../portfolio/service/PortfolioService";
import {
    extractOrderRequest,
    isSameBrokerageRequest
} from "../../../portfolio/templates/ticket/place-equity-order-wizard/util/EquityWizardOrderUtil";
import {PlaceOrderRequest} from "../../../portfolio/templates/ticket/services/models/PlaceOrderRequest";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {BrokerageEstimationService} from "../../../brokerage/services/BrokerageEstimationService";
import {ComponentCallback} from "../../../types/AngularJS";
import {OrderSide} from "../../models/OrderRecord";
import {EquityOrderType} from "../../../portfolio/templates/ticket/place-equity-order-wizard/models/EquityWizardData";
import {PricingInstruction} from "../../models/PricingInstruction";
import {ChildOrder} from "../../../portfolio/templates/ticket/place-order-wizard/models/ChildOrder";
import {QuoteViewModel} from "../../../quote/models/QuoteViewModel";

export const ChildOrdersDialog = "tfChildOrdersDialog";

export function childOrdersDialog(): IComponentOptions {
    return {
        template,
        bindings: {
            store: "<",
            modalInstance: "<",
            resolve: "<",
            onAccountBalanceChange: "&"
        },
        controllerAs: "ctrl",
        controller: ChildOrdersDialogComponentController
    };
}

interface Resolve {
    store: EquityOrderWizardStore;
    childOrder: ChildOrder;
    index: number | null;
}

export class ChildOrdersDialogComponentController implements IOnInit, IOnDestroy {

    /** @input */
    public store: EquityOrderWizardStore;
    /** @input */
    public readonly modalInstance: IModalInstanceService;
    /** @output */
    public readonly onAccountBalanceChange: ComponentCallback;
    public side: OrderSide;
    public parentLimit: number | undefined;
    public lastQuote: number | null;
    public quantity: number | null;
    public limit: number | undefined = 0;
    public consideration: number;
    public totalValue: number;
    public totalQuantity: number | null = 0;
    public targetTotalQuantity: number | null;
    public pricingInstruction: PricingInstruction;
    public childOrderIndex: number | null;
    public orderType: EquityOrderType = EquityOrderType.QUANTITY;
    public readonly code$ = new ReplaySubject<string>(1);
    public readonly quantityOwned$ = new BehaviorSubject(0);
    public readonly estimatedBrokerage$ = new BehaviorSubject<number | null>(null);
    private readonly destroyed$ = new ReplaySubject<boolean>(1);
    public readonly pricingInstruction$ = new BehaviorSubject<PricingInstruction>(PricingInstruction.LIMIT);
    public readonly isEdit$ = new ReplaySubject<boolean>(1);
    public readonly quote$ = new ReplaySubject<QuoteViewModel>(1);
    public readonly parentOrderLimitReached$ = new BehaviorSubject<boolean>(false);
    /** @input */
    public set resolve(resolve: Resolve) {
        if (resolve.store != null) {
            this.store = resolve.store;
        }
        if (resolve.childOrder != null) {
            this.limit = resolve.childOrder.limit;
            this.quantity = resolve.childOrder.quantity;
            this.pricingInstruction$.next(resolve.childOrder.pricingInstruction);
            this.consideration = (this.quantity ?? 0) * (this.limit ?? 0);
        } else {
            this.store.current.first().subscribe(c => {
                this.pricingInstruction$.next(c.pricingInstruction);
                this.limit = c.limit;
            });
            this.quantity = 0;
            this.consideration = 0;
        }
        this.childOrderIndex = resolve.index;
        this.isEdit$.next(resolve.childOrder != null);
    }

    /** @ngInject */
    public constructor(private readonly portfolioService: PortfolioService,
                       private readonly brokerageEstimationService: BrokerageEstimationService,
                       private readonly $timeout: ITimeoutService,
                       private $element: IRootElementService) {
    }

    public $onInit(): void {
        this.draggableInit();
        this.watchCode();
        this.watchQuantityOwnedAndAccountBalance();
        this.watchQuoteChange();
        this.watchBrokerageEstimate();
        this.watchOrderProperties();
        this.watchPricingInstruction();
    }

    public draggableInit() {
        this.$element.ready(  () =>  {
            this.$element.parent().parent().parent().draggable({ handle: ".modal-header" });
            this.$element.addClass("draggable-modal");
        });
    }

    private watchPricingInstruction() {
        this.pricingInstruction$.takeUntil(this.destroyed$)
            .subscribe(instruction => this.pricingInstruction = instruction);
    }

    private watchBrokerageEstimate() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .filter((order) => order.quantity !== 0)
            .map((order) => extractOrderRequest(order))
            .filter(isNotNull)
            .filter((request) => request.order.quantity != null && request.order.quantity !== 0)
            .distinctUntilChanged(isSameBrokerageRequest())
            .auditTime(1000)
            .switchMap((request) => this.retrieveBrokerageQuote(request.portfolioId, request.order))
            .subscribe((estimate) => this.estimatedBrokerage$.next(estimate));
    }

    private retrieveBrokerageQuote(portfolioId: number, request: PlaceOrderRequest): Observable<number | null> {
        const estimationRequest = ObjectUtil.pick(request, "code", "buy", "quantity", "limit", "legs", "pricingInstruction", "exchange");
        const response = this.brokerageEstimationService.estimate(portfolioId, estimationRequest);
        return Observable.fromPromise(response)
                         .catch(() => Observable.of(null));
    }

    private watchCode() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((d) => d.code)
            .filter(isNotNull)
            .distinctUntilChanged()
            .subscribe((code) => this.code$.next(code));
    }

    private watchQuoteChange() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((d) => d.quote)
            .filter(isNotNull)
            .distinctUntilChanged()
            .subscribe((quote) => {
                this.quote$.next(quote);
                this.lastQuote = quote.last;
            });
    }

    private fetchPositions(portfolioId: number): Observable<PortfolioPositions> {
        return Observable.of(portfolioId)
                         .flatMap((id) => this.portfolioService.portfolio(id))
                         .catch(() => Observable.empty<PortfolioPositions>());
    }

    private buildCurrentPortfolio(): Observable<PortfolioPositions> {
        return this.store
                   .current
                   .takeUntil(this.destroyed$)
                   .map((d) => d.portfolio)
                   .filter(isNotNull)
                   .map((account) => account.id)
                   .distinctUntilChanged()
                   .switchMap((id) => this.fetchPositions(id))
                   .map((p) => {
                       return p;
                   })
                   .publishReplay(1)
                   .refCount();
    }

    private watchQuantityOwnedAndAccountBalance() {
        const portfolioPositions$ = this.buildCurrentPortfolio();
        const equityPositions$ = portfolioPositions$.map(p => p.equityPositions);
        const code$ = this.store.current.map((d) => d.code).filter(isNotNull);
        const source$ = Observable.combineLatest(code$, equityPositions$);

        portfolioPositions$.takeUntil(this.destroyed$)
                           .subscribe((position) => this.onAccountBalanceChange({balance: position.currentCmaBalance + position.currentEquityLedger + position.currentEtoLedger }));

        source$.takeUntil(this.destroyed$)
               .map(([code, positions]) => this.findPositionQuantity(code, positions))
               .subscribe((quantity) => this.quantityOwned$.next(quantity));
    }
    private watchOrderProperties() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .subscribe((order) => this.$timeout(() => {
                this.side = order.side;
                this.parentLimit = order.limit;
                // this.quantity = order.quantity;
                // this.totalValue = order.totalValue;
                this.totalQuantity = order.childOrders.map((o, i) => this.childOrderIndex === i ? 0 : o.quantity)
                                          .reduce((total, qty) => (total ?? 0) + (qty ?? 0));
                this.targetTotalQuantity = order.quantity;
                // this.consideration = order.quantity * (order.limit ?? 0);
            }));
    }

    private findPositionQuantity(code: string, positions: ReadonlyArray<EquityPosition>): number {
        const position = positions.find((p) => p.code === code);
        if (position == null) {
            return 0;
        } else {
            return position.quantity;
        }
    }

    // public typeChanged(type: EquityOrderType) {
    //     this.store.updateOrderType(type);
    // }

    public totalValueChanged(totalValue: number) {
        this.store.updateTotalValue(totalValue);
    }

    public limitChanged(limit: number | null) {
        if (limit != null) {
            this.limit = limit;
            this.consideration = (this.quantity ?? 0) * (this.limit ?? 0);
            this.parentOrderLimitReached$.next(this.parentLimit != null && ((this.side === OrderSide.BUY && this.limit > this.parentLimit) ||
                (new Set([OrderSide.SHORT_SELL, OrderSide.LONG_SELL]).has(this.side)  && this.limit < this.parentLimit)));
        }
    }

    public qtyChanged(qty: number | null) {
        if (qty != null) {
            this.quantity = qty;
            this.consideration = (this.quantity ?? 0) * (this.limit ?? 0);
        }
    }

    public addChildOrder() {
        this.store.addChildOrder({quantity: this.quantity, pricingInstruction: this.pricingInstruction ?? PricingInstruction.LIMIT, limit: this.limit});
        this.limit = 0;
        this.quantity = 0;
        this.modalInstance.close();
    }

    public updateChildOrder() {
        if (this.childOrderIndex != null) {
            this.store.updateChildOrder(this.childOrderIndex, {quantity: this.quantity, pricingInstruction: this.pricingInstruction ?? PricingInstruction.LIMIT, limit: this.limit});
            this.limit = 0;
            this.quantity = 0;
            this.modalInstance.close();
        }
    }

    public pricingInstructionChanged(pricingInstruction: PricingInstruction) {
        if (PricingInstruction != null) {
            this.pricingInstruction$.next(pricingInstruction);
            if (pricingInstruction === PricingInstruction.MARKET_TO_LIMIT) {
                this.limitChanged(this.lastQuote);
            } else {
                this.limitChanged(this.parentLimit ?? null);
            }
        }
    }
    public closeModal() {
        this.limit = 0;
        this.quantity = 0;
        this.modalInstance.dismiss();
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
